<template>
    <MainViewContainer :showBackIcon="true">
        <div class="col-span-12 md:col-start-2 md:col-span-10 xl:col-span-9 survey-360-results">
            <div class="print-title-page">
                <img src="../assets/halsa-logo.png" alt="Hälsa" class="print-title-logo" />
                <div class="print-title-content">
                    <h1>{{ $t("default.survey360.title") }}<br />{{ subjectName }}</h1>
                </div>
            </div>
            <h1 class="print-hide">{{ translatedSurveyName }}: {{ subjectName }}</h1>
            <div class="flex md:items-center justify-between mt-6 print-hide mb-6">
                <div class="md:w-1/3">
                    <h2 class="mb-0">{{ $t("default.survey.results") }}</h2>
                </div>
                <div class="md:w-1/3 w-1/12"></div>
                <a class="button green" @click="printPage">
                    {{ $t("default.common.print") }}
                    <img class="icon-print" src="../assets/svg/print.svg" />
                </a>
            </div>
            <div class="grid grid-cols-3">
                <div class="flex">
                    <Loader v-if="loading"></Loader>
                </div>
            </div>
            <div v-if="surveyResult['survey_questions']" class="grid grid-cols-12 gap-1 mt-6 mb-6 print-parent">
                <SurveySummary360 v-if="
                    Object.keys(surveyResult['category_grouping']).length !== 0 &&
                    surveyResult['display_summary'] !== 0
                " :category_grouping="surveyResult['category_grouping']"
                    :display_summary="surveyResult['display_summary']"
                    :questions="surveyResult['survey_questions'].filter((q) => !q['exclude_from_summaries'])"
                    :clang="clang" title="Survey Summary" :chart-data="radarChartData"
                    :translated-roles="translatedRoles" @category-clicked="scrollTo" class="col-span-12" />

                <hr class="col-span-8 col-start-3" />

                <CategoryResults360 v-for="category in categoriesWithSummaries"
                    :questions="questionsForCategory(category)" :category="category" :key="`category-${category['id']}`"
                    :clang="clang" title="Category Summary" class="col-span-12" />

                <hr class="col-span-8 col-start-3 print-hide" />

                <div class="col-span-12" v-if="Object.keys(questionsPerCategory)"
                    style="break-before: page; page-break-before: always;">
                    <div v-for="category in Object.values(questionsPerCategory)"
                        :key="category[0]['category'] ? category[0]['category'].id : 'no-category'"
                        :id="`cat-${category[0]['category'] ? normalizeString(category[0]['category'].name) : 'nocategory'}`"
                        class="mt-8">
                        <h3>{{ categoryLabel(category[0]['category']) }}</h3>
                        <div v-for="(question, i) of category" :key="i" class="print-no-break">
                            <template v-if="question['answers'].length">
                                <div v-if="question['type'] === 'SO'" class="question-container flex w-full flex-row"
                                    :class="{ 'bg-gray-200': i % 2 }">
                                    <div class="flex md:w-2/6 question-text">
                                        {{ translator(clang, question) }}
                                    </div>
                                    <div class="md:w-4/6">
                                        <ChoiceQuestionResult v-for="(role, idx) in Object.keys(
                                            question['counts']
                                        ).sort((a, b) => {
                                            const aPos =
                                                roleSorting.findIndex((v) => v === a) ??
                                                roleSorting.length;
                                            const bPos =
                                                roleSorting.findIndex((v) => v === b) ??
                                                roleSorting.length;

                                            return aPos - bPos;
                                        })" :key="`${role}-${i}-${idx}`" :question="question" index=""
                                            :counts="question['counts'][role]" :clang="clang"
                                            :class="{ 'bg-gray-200': i % 2 }" :showQuestion="false"
                                            :role="translatedRoles[role]" title="Choice Question">
                                        </ChoiceQuestionResult>
                                    </div>
                                </div>
                                <div v-else-if="question['type'] === 'SC'"
                                    class="question-container md:flex w-full md:flex-row"
                                    :class="{ 'bg-gray-200': i % 2 }">
                                    <div class="md:w-2/6 question-text">
                                        {{ translator(clang, question) }}
                                    </div>
                                    <div class="mt-4 md:mt-0 md:w-4/6">
                                        <ScaleQuestionResult v-for="(role, idx) in Object.keys(
                                            question['average']
                                        ).sort((a, b) => {
                                            const aPos =
                                                roleSorting.findIndex((v) => v === a) ??
                                                roleSorting.length;
                                            const bPos =
                                                roleSorting.findIndex((v) => v === b) ??
                                                roleSorting.length;

                                            return aPos - bPos;
                                        })" :key="`${role}-${i}-${idx}`" :question="question" :showQuestion="false"
                                            :answers="role === 'all'
                                                ? question['answers']
                                                : question['answers'].filter((a) => a.role === role)
                                                " index="" :clang="clang" :average="question['average'][role]"
                                            :role="translatedRoles[role]" :counts="question['counts'][role]"
                                            title="Scale Question">
                                        </ScaleQuestionResult>
                                    </div>
                                </div>
                                <template v-else-if="['FT', 'ST'].indexOf(question['type']) > -1">
                                    <OpenQuestionResult title="Open Question" index="" :question="question"
                                        :clang="clang" :class="{ 'bg-gray-200': i % 2 }" :answers="question['answers']"
                                        :key="`OQ-${i}`" />
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </MainViewContainer>
</template>

<script setup>
import MainViewContainer from "@/components/MainViewContainer";
import ChoiceQuestionResult from "@/components/ChoiceQuestionResult";
import ScaleQuestionResult from "@/components/ScaleQuestionResult";
import OpenQuestionResult from "@/components/OpenQuestionResult";
import SurveySummary360 from "@/components/SurveySummary360";
import CategoryResults360 from "@/components/CategoryResults360";
import Loader from "@/components/Loader";

import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { roleSorting } from "../survey360Utils";

const props = defineProps({
    surveyResult: { type: Object, required: true },
    roles: { type: Array, required: true },
    clang: { type: String, required: true },
    loading: { type: Boolean, required: true },
    subjectName: { type: String, required: true },
});

const { t } = useI18n();

const radarColorByRole = {
    itse: "--halsa-blue",
    kollegat: "--halsa-green-dark",
    "tiimin jäsenet": "--halsa-red",
    esihenkilö: "--halsa-green",
    sidosryhmä: "--halsa-red-dark",
};

const getRadardChartColor = (role) => {
    return radarColorByRole[role] ?? "--halsa-blue";
};

const calculateAverage = (arr) => {
    const filtered = arr.filter((e) => !Number.isNaN(e));
    if (filtered.length < 1) return NaN;
    return filtered.reduce((acc, cur) => acc + cur) / filtered.length;
};

const getCssVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
};

const translator = (clang = "fi", questionArray) => {
    const reducedArray = {};
    questionArray.translations.forEach((item) => {
        reducedArray[item.locale] = item.text;
    });
    if (reducedArray[clang] !== undefined) return reducedArray[clang];
    if (reducedArray["fi"] !== undefined) return reducedArray["fi"];
    if (reducedArray["en"] !== undefined) return reducedArray["en"];
    if (reducedArray["sv"] !== undefined) return reducedArray["sv"];
    if (questionArray["name"] !== undefined) return questionArray["name"];
    return "";
};

const translatedRoles = computed(() => {
    const result = { all: t("default.survey360.roles.all") };
    props.roles.forEach((r) => {
        result[r.name] = translator(props.clang, r);
    });
    return result;
});

const translatedSurveyName = computed(() => {
    if (!props.surveyResult || Object.keys(props.surveyResult).length === 0) return "";
    const surveyTranslations = {
        name: props.surveyResult.survey.name,
        translations: props.surveyResult.survey.translations.map((tr) => ({
            locale: tr.locale,
            text: tr.name,
        })),
    };
    return translator(props.clang, surveyTranslations);
});

function groupBy(objectArray, property) {
    return objectArray.reduce((acc, obj) => {
        const key = obj[property];
        if (!acc[key]) acc[key] = [];
        acc[key].push(obj);
        return acc;
    }, {});
}

function questionsForCategory(category) {
    if (!("survey_questions" in props.surveyResult)) return [];
    return props.surveyResult["survey_questions"].filter(
        (x) =>
            x["answers"].length &&
            x["type"] === "SC" &&
            x["category"] &&
            x["category"]["id"] === category["id"]
    );
}

const noCategoryTranslations = {
    sv: "Ingen kategori",
    en: "No category",
    fi: "Ei kategoriaa",
};

function categoryLabel(category) {
    if (!category) {
        return noCategoryTranslations[props.clang] ?? noCategoryTranslations.fi;
    }
    return translator(props.clang, category);
}

function translateCategory(result) {
    const noCategory = {
        sv: "Ingen kategori",
        en: "No category",
        fi: "Ei luokkaa",
    };
    if (!result) {
        return noCategory[this.state.clan];
    }
    if (result.question === undefined || result.question === null) {
        return noCategory[this.state.clan];
    }
    const category = result.question.category;
    console.log("category trnslations", category)
    if (category === null) {
        return noCategory[this.state.clan];
    }
    return category.translations.length === 0
        ? category["name"]
        : translator(this.state.clan, category);
}

function normalizeString(str) {
    return str.replace(/[^a-zA-Z0-9]/g, "");
}

function scrollTo(categoryName) {
    const cat = normalizeString(categoryName);
    const target = document.getElementById(`cat-${cat}`);
    if (target) {
        target.scrollIntoView({ behavior: "smooth" });
    }
}

function printPage() {
    window.print();
}

const categoriesWithSummaries = computed(() => {
    if (!("category_grouping" in props.surveyResult)) return [];
    const newObj = Object.values(props.surveyResult["category_grouping"])
        .filter((cat) => cat["display_summary"] !== 0)
        .map((cat) => {
            const newQuestions = Object.values(cat.questions).map((question) => {
                const questionDefinition = props.surveyResult["survey_questions"].find(
                    (sq) => sq.id == question.id
                );
                const maxVal =
                    questionDefinition.steps.steps[
                        questionDefinition.steps.steps.length - 1
                    ].value;
                return { ...question, max: maxVal };
            });
            return { ...cat, questions: newQuestions };
        });
    console.dir(newObj);
    return newObj;
});

const questionsPerCategory = computed(() => {
    if (!props.surveyResult.survey_questions) return {};
    const order = props.surveyResult.survey.question_order;
    const sortedQuestions = [...props.surveyResult.survey_questions].sort(
        (a, b) => order.indexOf(a.id) - order.indexOf(b.id)
    );
    return groupBy(
        sortedQuestions.map((item) => ({
            ...item,
            cat: item.category ? item.category.name : "No category",
        })),
        "cat"
    );
});

const radarChartData = computed(() => {
    const categories = Object.values(props.surveyResult?.category_grouping ?? {});
    if (!categories.length) return {};
    const labels = categories.map((e) => translator(props.clang, e));
    const datasets = props.roles.map((r) => {
        const color = getCssVariable(getRadardChartColor(r.name));
        return {
            label: translator(props.clang, r),
            data: categories.map(
                (c) =>
                    calculateAverage(
                        Object.values(c.questions)
                            .map((q) => q.normalized_average[r.name] || NaN)
                            .filter((v) => !Number.isNaN(v))
                    ) * 100
            ),
            fill: true,
            borderColor: color,
            backgroundColor: color,
            pointBackgroundColor: color,
            pointBorderColor: "#fff",
        };
    });
    return { labels, datasets };
});
</script>

<style lang="scss">
@page {
    margin: 1.5cm;
}

@media print {
    header.navigation {
        display: none;
    }

    .main-container {
        min-height: 0;
    }

    .main-view-container {
        margin: 0;
        padding: 0;
    }

    .main-view-container main,
    .survey-360-results {
        display: block !important;
        width: 100% !important;
    }
}

.print-title-page {
    display: none;

    @media print {
        display: block;
        page-break-after: always;
        position: relative;

        .print-title-logo {
            height: 60px;
        }

        .print-title-content {
            padding-top: 35%;
            text-align: center;
        }
    }
}

.survey-360-results {
    .question-container {
        padding: 20px 12px;

        .question-text {
            font-size: 14px;
        }
    }

    /** FIXME: should merge these to a base.scss at some point. Also invite.vue has the same */
    select,
    option {
        padding: 0.5rem 1rem;
        line-height: 1.25;
        border: 2px solid rgba(229, 231, 235, 1);
        min-width: 20rem;
    }

    .category-summary-360:nth-child(even) {
        background: var(--gray-200);
    }

    .scale-question-result:nth-child(even) {
        .bar-chart {
            .background {
                .bar {
                    background: var(--halsa-blue);
                }
            }
        }
    }

    .icon-print {
        height: 1.2em;
        width: 1.2em;
    }
}
</style>
